import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Panel as RFPanel } from "@xyflow/react";
import { IconButton } from "@zapier/design-system-beta";
import { Text, Icon } from "@zapier/design-system";
import { useAtomValue } from "jotai";
import {
  nodeAtomFamily,
  NodeType,
  sideBarSelectedAtom,
} from "@zapier/canvas-state";
import classNames from "classnames";

import { determineAssetType } from "../../../utils/determineAssetType";

import styles from "./Panel.module.css";
import { ZapPreview } from "./ZapPreview";
import { TablePreview } from "./TablePreview";
import { InterfacePreview } from "./InterfacePreview";
import { ChatbotPreview } from "./ChatbotPreview";

interface RightPanelProps {
  onClose: () => void;
  title: string;
  closeOnEsc?: boolean;
  nodeId: string;
}

const assetContentMap = {
  zap: ZapPreview,
  table: TablePreview,
  interface: InterfacePreview,
  chatbot: ChatbotPreview,
};

export const Panel = ({
  onClose,
  title,
  closeOnEsc = true,
  nodeId,
}: RightPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const node = useAtomValue(nodeAtomFamily(nodeId));
  const isSideBarOpen = useAtomValue(sideBarSelectedAtom);
  const assetType = determineAssetType(
    (node.data as any).services,
    node.type as NodeType,
  );

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose, closeOnEsc]);

  if (!assetType) {
    return null;
  }

  const Content = assetContentMap[assetType];
  const previewContent = <Content nodeId={nodeId} />;

  const content = (
    <RFPanel
      className={classNames(styles.rightPanelWrapper, {
        [styles.sidebarOpen]: isSideBarOpen,
      })}
    >
      <div ref={panelRef} className={styles.rightPanel}>
        <div className={styles.rightPanelHeader}>
          <Icon name="miscBoltAltFill" size="small" color="BrandOrange" />
          <span className={styles.rightPanelTitle}>
            <Text type="ParagraphHeader3">{title}</Text>
          </span>
          <IconButton
            name="formX"
            onPress={onClose}
            variant="ghost"
            size="medium"
          />
        </div>
        <div className={styles.rightPanelContent}>{previewContent}</div>
      </div>
    </RFPanel>
  );

  // Create portal to attach to the end of the body
  return createPortal(content, document.body);
};
